import { useInfiniteQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import React, { useState } from 'react';

import { HealthcareEntitiesService } from '../../../js/api';
import { useDebounce } from '../hooks/useDebounce';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_MS = 250;

export type EntityChoice = { id: string; name: string };

export type HealthcareEntitySearchListProps = {
  onSelect: (entity: EntityChoice) => void;
  onClear?: () => void;
  showClear?: boolean;
  selectedId?: string | null;
  autoFocus?: boolean;
};

export const HealthcareEntitySearchList: React.FC<HealthcareEntitySearchListProps> = ({
  onSelect,
  onClear,
  showClear,
  selectedId,
  autoFocus,
}) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['acting-as-entity-picker', debouncedSearch],
    queryFn: ({ pageParam = 0 }) =>
      HealthcareEntitiesService.healthcareEntitiesList({
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset: pageParam as number,
      }),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.next ? allPages.length * PAGE_SIZE : undefined,
  });

  const loadMoreEntities = async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  };

  const { loadingRef } = useInfiniteScroll(loadMoreEntities, hasNextPage || false, {
    enabled: hasNextPage && !isLoading,
  });

  const entities = data?.pages.flatMap((page) => page.results || []) || [];
  const trimmedSearch = debouncedSearch.trim();

  return (
    <div className="tw:flex tw:flex-col tw:max-h-96 tw:w-80">
      <div className="tw:p-2 tw:border-b tw:border-zinc-200">
        <input
          type="text"
          /* eslint-disable-next-line jsx-a11y/no-autofocus */
          autoFocus={autoFocus}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search organizations…"
          className="tw:w-full tw:px-2 tw:py-1 tw:text-sm tw:border tw:border-zinc-300 tw:rounded tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-brand-green-1"
        />
      </div>
      <div className="tw:overflow-y-auto tw:flex-1 tw:py-1">
        {isLoading && <div className="tw:px-3 tw:py-2 tw:text-sm tw:text-zinc-500">Loading…</div>}
        {!isLoading && entities.length === 0 && (
          <div className="tw:px-3 tw:py-2 tw:text-sm tw:text-zinc-500">
            {trimmedSearch
              ? `No organizations match "${trimmedSearch}".`
              : 'No organizations available.'}
          </div>
        )}
        {entities.map((entity) => (
          <button
            key={entity.id}
            type="button"
            onClick={() => onSelect({ id: entity.id, name: entity.name })}
            className={clsx(
              'tw:block tw:w-full tw:text-left tw:px-3 tw:py-1.5 tw:text-sm tw:hover:bg-zinc-100',
              entity.id === selectedId && 'tw:!bg-brand-green-2 tw:font-medium'
            )}
          >
            {entity.name}
          </button>
        ))}
        {hasNextPage && (
          <div ref={loadingRef} className="tw:py-2 tw:text-center tw:text-xs tw:text-zinc-500">
            {isFetchingNextPage ? 'Loading more…' : ''}
          </div>
        )}
      </div>
      {showClear && onClear && (
        <div className="tw:p-2 tw:border-t tw:border-zinc-200">
          <button
            type="button"
            onClick={onClear}
            className="tw:w-full tw:text-left tw:text-sm tw:text-zinc-600 tw:hover:text-zinc-900 tw:px-2 tw:py-1"
          >
            <i className="far fa-fw fa-xmark tw:mr-1" />
            Clear selection
          </button>
        </div>
      )}
    </div>
  );
};
